// Catalogue des livres partenaires de La Nouvelle Librairie
// URLs validées manuellement - Données enrichies avec taxonomie ChatGPT

export interface PartnerBook {
  title: string;
  author?: string;
  pages?: number;
  price: string;
  url: string;
  // Structure tripartite avec pondération différenciée
  themes: string[];      // Poids: 3 points (génériques)
  authors: string[];     // Poids: 10 points (spécifiques)
  concepts: string[];    // Poids: 8 points (gardés intacts)
}

// Liste des mots à exclure du matching (stop words français)
const STOP_WORDS = new Set([
  'qui', 'que', 'quoi', 'quel', 'quelle', 'quels', 'quelles',
  'est', 'sont', 'etre', 'avoir', 'fait', 'faire',
  'les', 'des', 'une', 'uns', 'aux', 'dans', 'sur', 'sous', 'par', 'pour',
  'avec', 'sans', 'entre', 'vers', 'chez', 'contre',
  'son', 'ses', 'leur', 'leurs', 'notre', 'votre',
  'lui', 'elle', 'eux', 'elles', 'nous', 'vous', 'ils',
  'tout', 'tous', 'toute', 'toutes', 'autre', 'autres',
  'meme', 'memes', 'quelque', 'quelques',
  'plus', 'moins', 'tres', 'bien', 'peu', 'aussi', 'comme',
  'mais', 'donc', 'car', 'parce', 'quand', 'comment', 'pourquoi',
  'cette', 'cet', 'ces', 'cela', 'ceci',
]);

// Normalise le texte pour le matching (minuscules, sans accents, sans ponctuation)
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9\s-]/g, ' ')   // Garde les tirets pour concepts composés
    .replace(/\s+/g, ' ')
    .trim();
}

// Fallback : liste statique utilisée si les flux sont indisponibles
const FALLBACK_BOOKS: PartnerBook[] = [
  {
    title: "Un autre Rousseau : Lumières et contre-Lumières",
    author: "Alain de Benoist",
    pages: 302,
    price: "23,90€",
    url: "https://nouvelle-librairie.com/boutique/non-classes/un-autre-rousseau-lumieres-et-contre-lumieres/",
    themes: ["philosophie", "histoire", "politique"],
    authors: ["benoist", "rousseau"],
    concepts: ["lumieres", "contre-lumieres", "modernite"]
  },
  {
    title: "Les belles-mères sont-elles de droite ?",
    author: "Richard de Seze",
    pages: 168,
    price: "14,90€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/les-belles-meres-sont-elles-de-droite/",
    themes: ["politique", "societe", "culture"],
    authors: ["seze"],
    concepts: ["clivage-gauche-droite", "politisation-quotidien"]
  },
  {
    title: "Polémos notre père. Connaître la guerre, d'Héraclite à Julien Freund",
    author: "Aristide Leucate",
    pages: 88,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/polemos-notre-pere-connaitre-la-guerre-dheraclite-a-julien-freund/",
    themes: ["philosophie", "politique", "guerre"],
    authors: ["leucate", "heraclite", "freund"],
    concepts: ["polemos", "conflictualite"]
  },
  {
    title: "La Guerre des civilisations. Introduction à l'œuvre de Feliks Koneczny",
    author: "Antoine Dresse",
    pages: 104,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/la-guerre-des-civilisations-sous-titre-introduction-a-loeuvre-de-feliks-koneczny/",
    themes: ["histoire", "civilisation", "theorie"],
    authors: ["dresse", "koneczny"],
    concepts: ["choc-des-civilisations", "pluralite-civilisationnelle"]
  },
  {
    title: "La Source pérenne : Un parcours païen",
    author: "Christopher Gérard",
    pages: 266,
    price: "22,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/la-source-perenne-un-parcours-paien/",
    themes: ["spiritualite", "philosophie", "tradition"],
    authors: ["gerard"],
    concepts: ["paganisme", "tradition-europeenne"]
  },
  {
    title: "Le racisme antiblanc",
    author: "François Bousquet",
    pages: 292,
    price: "21,90€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/le-racisme-antiblanc/",
    themes: ["sociologie", "politique"],
    authors: ["bousquet"],
    concepts: ["racisme-antiblanc", "asymetrie-raciale", "antiracisme-institutionnel"]
  },
  {
    title: "De quoi Louis Sarkozy est-il le nom ?",
    author: "Rodolphe Cart",
    pages: 108,
    price: "14,50€",
    url: "https://nouvelle-librairie.com/boutique/politique/actualite/de-quoi-louis-sarkozy-est-il-le-nom-rodolphe-cart/",
    themes: ["politique", "medias"],
    authors: ["cart", "sarkozy"],
    concepts: ["personnalisation-politique", "heritage-pouvoir"]
  },
  {
    title: "Carl von Clausewitz : Soldat, réformateur, stratégiste",
    author: "Laurent Schang",
    pages: 64,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/histoire/histoire-moderne-et-contemporaine/carl-von-clausewitz-soldat-reformateur-strategiste/",
    themes: ["histoire", "strategie", "guerre"],
    authors: ["schang", "clausewitz"],
    concepts: ["theorie-militaire"]
  },
  {
    title: "Artificialités intelligentes",
    author: "Stefano VAJ",
    pages: 136,
    price: "14,90€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/artificialites-intelligentes/",
    themes: ["technologie", "philosophie", "anthropologie"],
    authors: ["vaj"],
    concepts: ["intelligence-artificielle", "transhumanisme", "posthumanisme", "biopolitique"]
  },
  {
    title: "Le Nouveau Monde des puissances – L'Heure de l'État-civilisation ?",
    author: "Gérard Dussouy",
    pages: 259,
    price: "20,90€",
    url: "https://nouvelle-librairie.com/boutique/politique/geopolitique/le-nouveau-monde-des-puissances-lheure-de-letat-civilisation/",
    themes: ["geopolitique", "civilisation"],
    authors: ["dussouy"],
    concepts: ["etat-civilisation", "multipolarite"]
  },
  {
    title: "Bienvenue Honorables Visiteurs",
    author: "Jean Raspail",
    pages: 288,
    price: "25,00€",
    url: "https://nouvelle-librairie.com/boutique/litterature/romans-poesie-theatre/jean-raspail-bienvenue-honorables-visiteurs/",
    themes: ["litterature", "satire"],
    authors: ["raspail"],
    concepts: ["choc-culturel", "declin-occident"]
  },
  {
    title: "L'Enthousiasme",
    author: "Rémi Soulié",
    pages: 174,
    price: "21,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/enthousiasme/",
    themes: ["poesie", "philosophie", "metaphysique"],
    authors: ["soulie"],
    concepts: ["inspiration-poetique", "sacre"]
  },
  {
    title: "Un chemin de pensée",
    author: "Collectif",
    pages: 708,
    price: "32,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/theorie-idees-histoire/un-chemin-de-pensee/",
    themes: ["philosophie", "politique"],
    authors: [],
    concepts: ["nouvelle-droite", "parcours-intellectuel", "pluralisme-ideologique"]
  },
  {
    title: "Maurice Barrès, le prince de la jeunesse",
    author: "Jeremy Baneton",
    pages: 72,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/biographie/maurice-barres-le-prince-de-la-jeunesse/",
    themes: ["biographie", "histoire"],
    authors: ["baneton", "barres"],
    concepts: ["nationalisme-francais", "engagement-intellectuel"]
  },
  {
    title: "La Crise de l'autorité dans l'Église",
    author: "Maxence Hecquard",
    pages: 600,
    price: "30,00€",
    url: "https://nouvelle-librairie.com/boutique/religion/la-crise-de-lautorite-dans-leglise/",
    themes: ["religion", "theologie"],
    authors: ["hecquard"],
    concepts: ["crise-eglise", "modernisme-religieux", "tradition-catholique"]
  },
  {
    title: "Prendre le maquis avec Ernst Jünger",
    author: "Éric Werner",
    pages: 120,
    price: "13,00€",
    url: "https://nouvelle-librairie.com/boutique/philosophie/15297/",
    themes: ["philosophie", "politique"],
    authors: ["werner", "junger"],
    concepts: ["anarchisme-droite", "liberte-interieure", "anti-conformisme"]
  },
  {
    title: "Alain de Benoist à l'endroit",
    author: "François Bousquet",
    pages: 180,
    price: "15,50€",
    url: "https://nouvelle-librairie.com/boutique/politique/theorie-idees-histoire/alain-de-benoist-a-lendroit/",
    themes: ["politique", "philosophie"],
    authors: ["bousquet", "benoist"],
    concepts: ["nouvelle-droite", "parcours-intellectuel"]
  },
  {
    title: "Allemagne : plaidoyer pour une droite identitaire",
    author: "Maximilian Krah",
    pages: 280,
    price: "21,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/allemagne-plaidoyer-pour-une-droite-identitaire/",
    themes: ["politique", "europe"],
    authors: ["krah"],
    concepts: ["droite-identitaire", "identite-nationale", "identite-europeenne"]
  },
  {
    title: "Feu sur la droite nationale !",
    author: "Rodolphe Cart",
    pages: 98,
    price: "12,00€",
    url: "https://nouvelle-librairie.com/boutique/militantisme/feu-sur-la-droite-nationale/",
    themes: ["politique", "militantisme"],
    authors: ["cart"],
    concepts: ["droite-nationale", "fractures-ideologiques"]
  },
  {
    title: "Formatage continu",
    author: "Xavier Eman",
    pages: 108,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/formatage-continu/",
    themes: ["medias", "societe", "journalisme"],
    authors: ["eman"],
    concepts: ["conformisme-mediatique", "formatage-ideologique"]
  },
  {
    title: "La Destruction des Européens d'Europe",
    author: "Renaud Camus",
    pages: 410,
    price: "28,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/la-destruction-des-europeens-deurope/",
    themes: ["civilisation", "identite", "demographie"],
    authors: ["camus"],
    concepts: ["grand-remplacement", "disparition-civilisationnelle"]
  },
  {
    title: "Pierre Drieu la Rochelle. Le rêve ou l'action",
    author: "Jeremy Baneton",
    pages: 82,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/biographie/pierre-drieu-la-rochelle-le-reve-ou-laction-jeremy-baneton/",
    themes: ["biographie", "litterature"],
    authors: ["baneton", "drieu", "rochelle"],
    concepts: ["engagement-intellectuel", "europeanisme"]
  },
  {
    title: "La Menace néo-conservatrice. Une France et une Europe sous influence",
    author: "Rodolphe Cart",
    pages: 78,
    price: "8,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/la-menace-neo-conservatrice-une-france-et-une-europe-sous-influence-rodolphe-cart/",
    themes: ["geopolitique", "politique"],
    authors: ["cart"],
    concepts: ["neoconservatisme", "atlantisme", "influence-americaine"]
  },
  {
    title: "Oswald Spengler. Introduction au Déclin de l'Occident",
    author: "David Engels",
    pages: 84,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/philosophie/oswald-spengler-introduction-au-declin-de-loccident/",
    themes: ["philosophie", "histoire", "civilisation"],
    authors: ["engels", "spengler"],
    concepts: ["declin-occident", "cycles-historiques", "morphologie-civilisationnelle"]
  },
  {
    title: "Que faire ? Vivre avec le déclin de l'Europe",
    author: "David Engels",
    pages: 178,
    price: "16,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/que-faire-vivre-avec-le-declin-de-leurope/",
    themes: ["europe", "politique"],
    authors: ["engels"],
    concepts: ["declin-europeen", "survie-civilisationnelle"]
  },
  {
    title: "Cahier d'étude pour une pensée européenne n°1 : Europe",
    author: "Collectif",
    pages: 275,
    price: "25,00€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/cahier-detude-pour-une-pensee-europeenne-n1-europe/",
    themes: ["europe", "histoire", "culture"],
    authors: [],
    concepts: ["pensee-europeenne", "iliade", "heritage-historique"]
  },
  {
    title: "Nicolas Machiavel. Le patriote aux vertus romaines",
    author: "Valerio Benedetti",
    pages: 72,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/philosophie/nicolas-machiavel/",
    themes: ["philosophie", "politique"],
    authors: ["benedetti", "machiavel"],
    concepts: ["raison-etat", "realisme-politique"]
  },
  {
    title: "Les Indo-Européens",
    author: "Henri Levavasseur",
    pages: 96,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/histoire/les-indo-europeens/",
    themes: ["histoire", "anthropologie"],
    authors: ["levavasseur"],
    concepts: ["indo-europeens", "proto-histoire", "migrations-anciennes"]
  },
  {
    title: "Sparte et l'idée spartiate",
    author: "Frédéric Eparvier",
    pages: 74,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/histoire/histoire-antique/sparte-et-lidee-spartiate/",
    themes: ["histoire", "antiquite"],
    authors: ["eparvier"],
    concepts: ["sparte", "modele-spartiate", "education-civique"]
  },
  {
    title: "Europe versus Occident",
    author: "Adriano Scianca",
    pages: 88,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/geopolitique/europe-versus-occident/",
    themes: ["geopolitique", "europe"],
    authors: ["scianca"],
    concepts: ["europe-vs-occident", "identite-europeenne"]
  },
  {
    title: "Saint-Exupéry au-delà du Petit Prince",
    author: "Philippe de Laitre",
    pages: 184,
    price: "15,50€",
    url: "https://nouvelle-librairie.com/boutique/litterature/saint-exupery-au-dela-du-petit-prince/",
    themes: ["litterature", "biographie"],
    authors: ["laitre", "saint-exupery", "exupery"],
    concepts: ["humanisme", "petit-prince"]
  },
  {
    title: "Nouveaux lieux communs",
    author: "Richard Millet",
    pages: 240,
    price: "21,90€",
    url: "https://nouvelle-librairie.com/boutique/politique/nouveaux-lieux-communs/",
    themes: ["politique", "societe"],
    authors: ["millet"],
    concepts: ["lieux-communs", "conformisme-culturel"]
  },
  {
    title: "L'extrême droite expliquée à Marie-Chantal",
    author: "Frédéric Saint-Clair",
    pages: 252,
    price: "18,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/lextreme-droite-expliquee-a-marie-chantal/",
    themes: ["politique", "pedagogie"],
    authors: ["saint-clair"],
    concepts: ["extreme-droite", "typologie-ideologique"]
  },
  {
    title: "Le réalisme politique",
    author: "Antoine Dresse",
    pages: 72,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/le-realisme-politique/",
    themes: ["politique", "philosophie"],
    authors: ["dresse"],
    concepts: ["realisme-politique", "interet-national", "equilibre-puissances"]
  },
  {
    title: "Années décisives",
    author: "Oswald Spengler",
    pages: 298,
    price: "23,00€",
    url: "https://nouvelle-librairie.com/boutique/histoire/histoire-moderne-et-contemporaine/annees-decisives/",
    themes: ["histoire"],
    authors: ["spengler"],
    concepts: ["crise-historique", "fin-cycle"]
  },
  {
    title: "Le combat culturel. Affirmer son identité face à l'universalisme moral",
    author: "Lothar Fritze",
    pages: 418,
    price: "22,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/theorie-idees-histoire/le-combat-culturel-affirmer-son-identite-face-a-luniversalisme-moral/",
    themes: ["politique", "culture", "identite"],
    authors: ["fritze"],
    concepts: ["combat-culturel", "hegemonie-culturelle", "affirmation-identitaire"]
  },
  {
    title: "Les nobles voyageurs",
    author: "Christopher Gérard",
    pages: 466,
    price: "24,50€",
    url: "https://nouvelle-librairie.com/boutique/litterature/les-nobles-voyageurs/",
    themes: ["litterature", "voyage"],
    authors: ["gerard"],
    concepts: ["recit-voyage", "regard-civilisationnel"]
  },
  {
    title: "Marine Le Pen comme je l'imaginais",
    author: "Vincent Coussedière",
    pages: 130,
    price: "11,90€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/dans-larene/marine-le-pen-comme-je-limaginais-vincent-coussediere/",
    themes: ["politique"],
    authors: ["coussediere"],
    concepts: ["incarnation-politique", "leadership-populaire", "marine-le-pen", "rassemblement-national"]
  },
  {
    title: "Mélenchon, le bruit et la fureur",
    author: "Rodolphe Cart",
    pages: 320,
    price: "18,90€",
    url: "https://nouvelle-librairie.com/boutique/editions-la-nouvelle-librairie/dans-larene/melenchon-le-bruit-et-la-fureur-portraits-d-un-revolutionnaire-rodolphe-cart/",
    themes: ["politique"],
    authors: ["cart"],
    concepts: ["populisme-de-gauche", "theatralisation-politique", "radicalite-discursive", "melenchon", "lfi", "gauche"]
  },
  {
    title: "Au-delà des droits de l'homme, pour défendre les libertés",
    author: "Alain de Benoist",
    pages: 186,
    price: "19,00€",
    url: "https://nouvelle-librairie.com/boutique/philosophie/au-dela-des-droits-de-lhomme-pour-defendre-les-libertes-2/",
    themes: ["philosophie", "politique"],
    authors: ["alain-de-benoist"],
    concepts: ["droits-homme", "libertes-concretes", "universalisme-abstrait"]
  },
  {
    title: "La République n'a pas besoin de savants / Entretiens avec Ludovic Maubreuil",
    author: "Michel Marmin",
    pages: 282,
    price: "20,90€",
    url: "https://nouvelle-librairie.com/boutique/arts/cinema/la-republique-na-pas-besoin-de-savants-entretiens-avec-ludovic-maubreuil/",
    themes: ["culture", "politique"],
    authors: ["marmin", "maubreuil"],
    concepts: ["critique-technocratie", "cinema-politique", "savoir-et-pouvoir"]
  },
  {
    title: "Fin de partie : requiem pour l'élection présidentielle",
    author: "Vincent Coussedière",
    pages: 200,
    price: "19,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/fin-de-partie-requiem-pour-lelection-presidentielle/",
    themes: ["politique"],
    authors: ["coussediere"],
    concepts: ["crise-representative", "epuisement-electoral", "democratie-procedurale", "presidentielle"]
  },
  {
    title: "De l'antiracisme comme terreur littéraire",
    author: "Richard Millet",
    pages: 92,
    price: "14,90€",
    url: "https://nouvelle-librairie.com/boutique/litterature/de-lantiracisme-comme-terreur-litteraire/",
    themes: ["litterature", "politique"],
    authors: ["millet"],
    concepts: ["antiracisme-ideologique", "censure-symbolique", "terreur-morale", "sos-racisme"]
  },
  {
    title: "Ezra Pound et le sacré",
    author: "Adriano Scianca",
    pages: 112,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/philosophie/ezra-pound-et-le-sacre/",
    themes: ["philosophie", "litterature"],
    authors: ["scianca"],
    concepts: ["sacre-poetique", "tradition-moderne", "metaphysique-esthetique", "ezra-pound"]
  },
  {
    title: "Academia Christiana, une aventure catholique et enracinée",
    author: "Collectif",
    pages: 224,
    price: "22,00€",
    url: "https://nouvelle-librairie.com/boutique/militantisme/academia-christiana-une-aventure-catholique-et-enracinee/",
    themes: ["religion", "militantisme"],
    authors: ["collectif", "academia-christiana"],
    concepts: ["engagement-catholique", "enracinement", "contre-modernite", "christianisme", "catholicisme"]
  },
  {
    title: "Comprendre la stratégie hongroise",
    author: "Balázs Orbán",
    pages: 328,
    price: "20,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/geopolitique/comprendre-la-strategie-hongroise/",
    themes: ["politique", "geopolitique", "europe-centrale"],
    authors: ["orban"],
    concepts: ["souverainete-nationale", "etat-stratege", "illiberalisme", "hongrie", "orbanisme"]
  },
  {
    title: "La Tradition sans complexe",
    author: "Arnaud Guyot-Jeannin",
    pages: 186,
    price: "15,50€",
    url: "https://nouvelle-librairie.com/boutique/politique/theorie-idees-histoire/la-tradition-sans-complexe/",
    themes: ["philosophie", "politique", "tradition"],
    authors: ["guyot-jeannin"],
    concepts: ["tradition-vivante", "critique-modernite", "heritage-europeen", "anti-relativisme"]
  },
  {
    title: "Réflexions sur la violence",
    author: "Georges Sorel",
    pages: 404,
    price: "25,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/reflexions-sur-la-violence/",
    themes: ["philosophie", "politique", "theorie-revolutionnaire"],
    authors: ["sorel"],
    concepts: ["violence-politique", "mythe-social", "syndicalisme-revolutionnaire", "critique-parlementarisme"]
  },
  {
    title: "Georges Sorel, le révolutionnaire conservateur",
    author: "Rodolphe Cart",
    pages: 216,
    price: "16,50€",
    url: "https://nouvelle-librairie.com/boutique/politique/theorie-idees-histoire/georges-sorel-le-revolutionnaire-conservateur/",
    themes: ["politique", "histoire-des-idees"],
    authors: ["cart", "sorel"],
    concepts: ["revolution-conservatrice", "critique-liberalisme", "genealogie-intellectuelle"]
  },
  {
    title: "Dominique Venner 2013-2023. La flamme se maintient",
    author: "Collectif",
    pages: 96,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/biographie/dominique-venner-2013-2023-la-flamme-se-maintient/",
    themes: ["biographie", "hommage", "memoire"],
    authors: ["venner"],
    concepts: ["fidelite-ideologique", "memoire-militante", "europe-identitaire"]
  },
  {
    title: "Frédéric Mistral. Patrie charnelle et Provence absolue",
    author: "Rémi Soulié",
    pages: 74,
    price: "9,00€",
    url: "https://nouvelle-librairie.com/boutique/biographie/frederic-mistral-patrie-charnelle-et-provence-absolue/",
    themes: ["biographie", "litterature", "identite-regionale"],
    authors: ["soulie", "mistral"],
    concepts: ["patrie-charnelle", "enracinement", "provence", "tradition-poetique"]
  },
  {
    title: "Pourquoi me persécutes-tu ? Chroniques d'un Orient martyr",
    author: "Charles de Meyer",
    pages: 234,
    price: "16,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/chroniques/pourquoi-me-persecutes-tu-chroniques-dun-orient-martyr/",
    themes: ["geopolitique", "religion", "temoignage"],
    authors: ["de-meyer"],
    concepts: ["chretiens-orient", "persecutions-religieuses", "orient-chretien", "violence-islamiste"]
  },
  {
    title: "Mes idées politiques",
    author: "Charles Maurras",
    pages: 322,
    price: "20,00€",
    url: "https://nouvelle-librairie.com/boutique/politique/theorie-idees-histoire/mes-idees-politiques-2/",
    themes: ["politique", "theorie-politique"],
    authors: ["maurras"],
    concepts: ["nationalisme-integral", "monarchie", "anti-parlementarisme", "tradition-politique-francaise"]
  },
];

// Pondération du scoring
const WEIGHTS = {
  author: 10,    // Très spécifique
  concept: 8,    // Concepts intacts
  title: 5,      // Titre du livre
  theme: 3,      // Génériques
};

// Seuil minimum de pertinence (requiert au moins 1 auteur, 1 concept, ou 2 thèmes)
const MIN_SCORE_THRESHOLD = 6;

// Calcule un score de pertinence entre une question et un livre
function calculateScore(queryWords: string[], book: PartnerBook): number {
  let specificScore = 0; // Score des matchs spécifiques (auteur, titre, concept)
  let themeScore = 0;    // Score des thèmes (génériques)
  
  for (const word of queryWords) {
    // Auteurs tableau (poids max - très spécifique)
    if (book.authors.some(a => normalize(a) === word)) {
      specificScore += WEIGHTS.author;
    }
    
    // Auteur champ texte (nom complet, ex: "Alain de Benoist")
    if (book.author) {
      const authorWords = normalize(book.author)
        .split(/[\s-]+/)
        .filter(w => w.length > 2 && !STOP_WORDS.has(w));
      if (authorWords.some(aw => aw === word)) {
        specificScore += WEIGHTS.author;
      }
    }
    
    // Titre du livre
    if (book.title) {
      const titleWords = normalize(book.title)
        .split(/[\s-]+/)
        .filter(w => w.length > 2 && !STOP_WORDS.has(w));
      if (titleWords.some(tw => tw === word)) {
        specificScore += WEIGHTS.title;
      }
    }
    
    // Thèmes (poids standard - ne comptent que comme bonus)
    if (book.themes.some(t => normalize(t) === word)) {
      themeScore += WEIGHTS.theme;
    }
  }
  
  // Concepts composés : évaluation globale (tous les mots du concept doivent être présents)
  for (const concept of book.concepts) {
    const conceptWords = normalize(concept)
      .split(/[\s-]+/)
      .filter(w => w.length > 2 && !STOP_WORDS.has(w));
    
    if (conceptWords.length === 0) continue;
    
    if (conceptWords.length === 1) {
      // Concept d'un seul mot : match exact
      if (queryWords.includes(conceptWords[0])) {
        specificScore += WEIGHTS.concept;
      }
    } else {
      // Concept composé : TOUS les mots doivent être présents dans la requête
      const allPresent = conceptWords.every(cw => queryWords.includes(cw));
      if (allPresent) {
        specificScore += WEIGHTS.concept;
      }
    }
  }
  
  // Les thèmes ne comptent que s'il y a au moins un match spécifique (auteur, titre, concept)
  // Cela évite qu'un thème générique comme "politique" suffise seul à afficher un livre
  return specificScore > 0 ? specificScore + themeScore : 0;
}

// === Gestion dynamique des livres partenaires ===

let loadedBooks: PartnerBook[] = [];

/**
 * Définit les livres chargés dynamiquement depuis les flux partenaires.
 * Appelé par usePartnerBooks() au démarrage de l'application.
 */
export function setPartnerBooks(books: PartnerBook[]): void {
  loadedBooks = books;
  console.log(`[PartnerBooks] Loaded ${books.length} books from feeds`);
}

/**
 * Retourne les livres dynamiques si disponibles, sinon le fallback statique.
 */
export function getPartnerBooks(): PartnerBook[] {
  return loadedBooks.length > 0 ? loadedBooks : FALLBACK_BOOKS;
}

// Trouve les livres correspondant à une question (max 2 résultats)
export function findMatchingBooks(question: string): PartnerBook[] {
  const normalizedQuestion = normalize(question);
  const words = normalizedQuestion
    .split(' ')
    .filter(w => w.length > 2 && !STOP_WORDS.has(w));
  
  if (words.length === 0) return [];
  
  // Calcule le score pour chaque livre
  const scoredBooks = getPartnerBooks().map(book => ({
    book,
    score: calculateScore(words, book)
  }));
  
  // Filtre et trie par score décroissant
  return scoredBooks
    .filter(item => item.score >= MIN_SCORE_THRESHOLD)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2) // Maximum 2 livres
    .map(item => item.book);
}
